import { default as Box } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { AppButton } from 'components/AppButton';
import { ContentCard } from 'components/Card/ContentCard';
import { PageHeader } from 'components/PageHeader';
import { Text } from 'components/Text';
import { SettingsGuide } from 'modules/geolocation-settings-guide';
import { getTrans } from 'modules/i18n';
import { Layout } from 'modules/minimal-ui-theme';
import {
  GEOLOCATION_STATUS_MAP,
  GeolocationPermissionStatus,
  StatusBadge
} from 'modules/status-badge';
import { FeatureFlag, useFeatureToggle } from 'modules/unleash';
import { getUserDevice } from 'utils/device';
import { useGeolocation } from 'utils/geolocation';

const Bold = styled('b')(() => ({
  display: 'inline',
  whiteSpace: 'normal',
  wordWrap: 'break-word',
  wordBreak: 'break-word'
}));

const renderPendingDescription = () => {
  const p1 = getTrans(
    'geolocationSettingsPage.pendingStatusDescription.p1.part1'
  );
  const p2 = getTrans(
    'geolocationSettingsPage.pendingStatusDescription.p1.part2'
  );
  const p3 = getTrans(
    'geolocationSettingsPage.pendingStatusDescription.p1.part3'
  );
  const p4 = getTrans(
    'geolocationSettingsPage.pendingStatusDescription.p1.part4'
  );
  return (
    <>
      <Text variant="body2">
        {p1} <Bold>{p2}</Bold> {p3} <Bold>{p4}</Bold>
      </Text>
      <Text variant="body2" mt={2}>
        {getTrans('geolocationSettingsPage.pendingStatusDescription.p2')}
      </Text>
    </>
  );
};

const renderDescription = (renderMobileDescription: boolean) => {
  const p1 = getTrans('geolocationSettingsPage.statusDescription.p1.part1');
  const p2 = getTrans('geolocationSettingsPage.statusDescription.p1.part2');
  const p3 = getTrans('geolocationSettingsPage.statusDescription.p1.part3');
  const p4 = getTrans('geolocationSettingsPage.statusDescription.p1.part4');
  const p5 = getTrans('geolocationSettingsPage.statusDescription.p2.part1');
  const p6 = getTrans('geolocationSettingsPage.statusDescription.p2.part2');
  const p7 = getTrans('geolocationSettingsPage.statusDescription.p2.part3');
  const p8 = getTrans('geolocationSettingsPage.statusDescription.p4');

  return (
    <>
      <Text variant="body2">
        {p1} <b>{p2}</b> {p3} <b>{p4}</b>
      </Text>
      <Text variant="body2" mt={2}>
        {p5} <b>{p6}</b> {p7}
      </Text>
      {renderMobileDescription && (
        <Text variant="body2" mt={2}>
          {p8}
        </Text>
      )}
    </>
  );
};

export const GeolocationPermissionsPage = () => {
  const { isEnabled: isGeolocationEnabled } = useFeatureToggle(
    FeatureFlag.PRO_TALENT_IS_GEOLOCATION_ENABLED
  );

  const { permissionStatus, isPending, getLocation } = useGeolocation({});
  const { isDesktop } = getUserDevice(navigator?.userAgent);

  const badgeStatus = permissionStatus as GeolocationPermissionStatus;

  return (
    <Layout
      header={
        <PageHeader
          title={getTrans('geolocationSettingsPage.title')}
          withBackBtn
        />
      }
      childrenStyles={{ p: 0 }}
      withFooter
    >
      {isGeolocationEnabled && (
        <Box sx={{ mt: 13, ml: 2, mr: 2, mb: 8 }}>
          <ContentCard sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between">
              <Text variant="subtitle1" component="div" sx={{ mb: 1 }}>
                {getTrans('geolocationSettingsPage.statusLabel')}
              </Text>
              {badgeStatus && (
                <StatusBadge
                  status={badgeStatus}
                  statusMap={GEOLOCATION_STATUS_MAP}
                />
              )}
            </Box>
            <Box mt={2}>
              {isPending
                ? renderPendingDescription()
                : renderDescription(!isDesktop)}
            </Box>
            {isPending && (
              <AppButton
                color="primary"
                variant="contained"
                onClick={() => getLocation()}
                fullWidth={true}
                sx={{ mt: 2 }}
              >
                {getTrans('geolocationSettingsPage.activationButton')}
              </AppButton>
            )}
          </ContentCard>
          <SettingsGuide />
        </Box>
      )}
    </Layout>
  );
};
